import { EVM_SUPERSTATE_FUND_TOKENS, SVM_SUPERSTATE_TOKENS } from '@/constants/addresses';
import { Issuers } from '@/constants/issuers';

import { useMatchaWallets } from '@/hooks/useMatchaWallets';
import { useSuperstateFundAllowlist } from '@/hooks/useSuperstateFundAllowlist';
import { useSuperstateSolanaAllowlist } from '@/hooks/useSuperstateSolanaAllowlist';

import { fontStyles } from '@/styles/typography';

import { WarningCircleSolidIcon } from '@/ui/Icons/WarningCircleSolidIcon';
import { ExternalLink } from '@/ui/Link/ExternalLink';

import type { TokenResult } from '@/utils/0x/token-registry.types';
import type { EthereumAddress, SolanaAddress } from '@/utils/models';

type Props = {
  buyToken?: TokenResult;
  sellToken?: TokenResult;
};

export const SuperstateKYCAlert = ({ buyToken, sellToken }: Props) => {
  // wallet addresses
  const { ethereumWallet, solanaWallet } = useMatchaWallets();
  const evmAddress = ethereumWallet?.address as EthereumAddress;
  const solanaAddress = solanaWallet?.address as SolanaAddress;

  // Check if trading Superstate tokens
  const isTradingSuperstateToken =
    sellToken?.issuer?.name === Issuers.SUPERSTATE || buyToken?.issuer?.name === Issuers.SUPERSTATE;

  // Check EVM fund tokens
  const sellTokenInEVMFundList =
    sellToken?.address && EVM_SUPERSTATE_FUND_TOKENS.includes(sellToken.address.toLowerCase());
  const buyTokenInEVMFundList =
    buyToken?.address && EVM_SUPERSTATE_FUND_TOKENS.includes(buyToken.address.toLowerCase());
  const evmFundToken = sellTokenInEVMFundList
    ? sellToken
    : buyTokenInEVMFundList
      ? buyToken
      : undefined;

  // Check Solana Superstate tokens
  const sellTokenInSolanaList =
    sellToken?.address && SVM_SUPERSTATE_TOKENS.includes(sellToken.address);
  const buyTokenInSolanaList =
    buyToken?.address && SVM_SUPERSTATE_TOKENS.includes(buyToken.address);
  const isTradingSolanaSuperstateToken = sellTokenInSolanaList || buyTokenInSolanaList;

  // Check EVM allowlist for fund tokens
  const { data: isEVMAllowlisted } = useSuperstateFundAllowlist(evmAddress, evmFundToken?.symbol);
  const userIsAllowlistedForEVMFund = evmFundToken && isEVMAllowlisted;

  // Check Solana allowlist for Solana Superstate tokens
  const { data: isSolanaAllowlisted } = useSuperstateSolanaAllowlist(
    isTradingSolanaSuperstateToken ? solanaAddress : undefined,
  );
  const userIsAllowlistedForSolana = isTradingSolanaSuperstateToken && isSolanaAllowlisted;

  // Don't show alert if:
  // 1. Not trading any Superstate token, OR
  // 2. Trading an EVM fund token AND user is allowlisted on EVM, OR
  // 3. Trading a Solana Superstate token AND user is allowlisted on Solana
  if (!isTradingSuperstateToken || userIsAllowlistedForEVMFund || userIsAllowlistedForSolana) {
    return null;
  }

  return (
    <div
      className="mx-6 mt-6 rounded-md py-3.5 px-4 flex flex-col gap-2 bg-token-gray-300"
      role="alert"
    >
      <div
        className={fontStyles({
          variant: 'LABEL_MD',
          class: ['mb-1 flex items-center gap-2 after:content-none before:content-none'],
        })}
      >
        <WarningCircleSolidIcon className="size-4" />
        Trading Requires Verification
      </div>
      <div className={fontStyles({ variant: 'BODY_MD', class: ['text-token-gray-1000'] })}>
        Trading is only available to users who register an account with{' '}
        <ExternalLink href="https://superstate.com" className="underline">
          Superstate
        </ExternalLink>{' '}
        or a supported third-party wallet, and complete KYC verification.
        <p className="mt-2 font-bold">
          <ExternalLink href="https://superstate.com/register">Register</ExternalLink>
        </p>
      </div>
    </div>
  );
};
